import React, { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import StatsAPI from "@/src/api/StatsAPI";

import {
  getLiveViewersRefetchInterval,
  liveViewersQuery,
} from "./contentItem.queries";
import {
  buildLiveViewerPointList,
  getLiveTimeDomain,
  getLiveViewerSummary,
} from "./contentItem.utils";
import ChartCard from "../charts/ChartCard";
import { getChartCardStatus } from "../charts/charts.utils";
import ExportCsvButton from "../charts/ExportCsvButton";
import LiveViewersChart from "../charts/LiveViewersChart";
import StatCard, { formatCountOrEmpty } from "../kpi/StatCard";

import type { JSX } from "react";

import type { QueryStatus } from "../kpi/StatCard";

interface LiveViewersSectionProps {
  contentId: string;
  beginDate: string | undefined;
  liveEnd: string | null | undefined;
  isOnAir?: boolean;
}

function LiveViewersSection({
  contentId,
  beginDate,
  liveEnd,
  isOnAir,
}: LiveViewersSectionProps): JSX.Element {
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const { t, i18n } = useTranslation("editor-analytics");
  const lang = i18n.language;
  const query = useQuery({
    ...liveViewersQuery(projectSlug, contentId),
    refetchInterval: getLiveViewersRefetchInterval(!!isOnAir),
  });
  const { data, isLoading, isError } = query;
  // Polling refetches every few seconds: never dim the cards while it runs.
  const queryStatus: QueryStatus = { isLoading, isError, isFetching: false };

  const pointList = useMemo(
    () => buildLiveViewerPointList(data?.data ?? []),
    [data],
  );
  const summary = getLiveViewerSummary(pointList);
  const domain = getLiveTimeDomain({
    beginDate,
    liveEnd,
    pointList,
    isOnAir: !!isOnAir,
    now: Date.now(),
  });
  const fetchCsv = async (): Promise<BlobPart> =>
    StatsAPI.getLiveViewers({
      liveUuid: contentId,
      projectSlug,
      isExport: true,
    });

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-4 @3xl:grid-cols-4">
        {isOnAir ? (
          <StatCard
            label={t("contentItem.kpi.liveViewers")}
            query={queryStatus}
            value={formatCountOrEmpty(summary.current, lang)}
          />
        ) : null}
        <StatCard
          label={t("contentItem.kpi.peakViewers")}
          query={queryStatus}
          value={formatCountOrEmpty(summary.peak, lang)}
        />
        <StatCard
          label={t("contentItem.kpi.uniqueViewers")}
          query={queryStatus}
          value={formatCountOrEmpty(summary.unique, lang)}
        />
      </div>
      <ChartCard
        title={t("contentItem.charts.liveViewers")}
        actions={
          <ExportCsvButton fetchCsv={fetchCsv} fileName="live_viewers.csv" />
        }
        status={getChartCardStatus({
          isLoading,
          isError,
          isEmpty: !pointList.length,
        })}
      >
        <LiveViewersChart
          domain={domain}
          endTimestamp={liveEnd ? moment.utc(liveEnd).valueOf() : null}
          label={t("contentItem.series.viewers")}
          pointList={pointList}
        />
      </ChartCard>
    </div>
  );
}

export default LiveViewersSection;
